import { useState, useCallback, useEffect } from 'react';
import { authApi } from '../lib/supabase';

const TOKEN_KEY = 'lawnapp_token';
const USER_ID_KEY = 'lawnapp_user_id';

export interface AuthUser {
  id: string;
  email: string;
  fullName?: string;
  avatarUrl?: string;
  selectedCity?: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const clearAuth = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_ID_KEY);
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await authApi.login(email, password);
      
      if (result.user) {
        const user = result.user;
        localStorage.setItem(USER_ID_KEY, user.id);
        
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        return { success: true };
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed';
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  }, []);

  const register = useCallback(async (email: string, password: string, fullName: string): Promise<{ success: boolean; error?: string }> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await authApi.register(email, password, fullName);
      
      if (result.user) {
        const user = result.user;
        localStorage.setItem(USER_ID_KEY, user.id);
        
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        return { success: true };
      } else {
        throw new Error('Registration failed');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Registration failed';
      setState((prev) => ({ ...prev, isLoading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  }, []);

  const logout = useCallback(() => {
    authApi.logout();
    clearAuth();
  }, [clearAuth]);

  const getToken = useCallback((): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userId = localStorage.getItem(USER_ID_KEY);
    
    if (token && userId) {
      authApi.getCurrentUser().then(user => {
        if (user) {
          setState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          clearAuth();
        }
      }).catch(() => {
        clearAuth();
      });
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [clearAuth]);

  const updateUser = useCallback(async (updates: { fullName?: string; avatarUrl?: string; selectedCity?: string }) => {
    if (!state.user?.id) return;

    try {
      const user = await authApi.updateProfile(updates);
      setState((prev) => {
        if (!prev.user) return prev;
        return { ...prev, user };
      });
    } catch (err: any) {
      console.error('Error updating user:', err);
    }
  }, [state.user?.id]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    login,
    register,
    logout,
    getToken,
    updateUser,
    checkAuth,
  };
};

export default useAuth;